// AuthContext: provides authentication state and helpers (login, register, logout)
// Uses HTTP-only cookies for secure token management

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import authApi from "../api/auth";

export const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateProfile: async () => {},
  isAdmin: () => false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session on load (cookie sent automatically)
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const response = await authApi.getProfile();

        if (isMounted) {
          setUser(response.data.user);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // Login
  const login = useCallback(async ({ email, password }) => {
    setError(null);

    try {
      const response = await authApi.login({ email, password });
      const { user: loggedUser } = response.data;

      setUser(loggedUser);

      return { success: true, user: loggedUser };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Erreur de connexion";
      setError(message);

      return { success: false, error: message };
    }
  }, []);

  // Register
  const register = useCallback(
    async ({ name, email, password, password_confirmation }) => {
      setError(null);

      try {
        const response = await authApi.register({
          name,
          email,
          password,
          password_confirmation,
        });

        const { user: newUser } = response.data;

        setUser(newUser);

        return { success: true, user: newUser };
      } catch (err) {
        const message =
          err.response?.data?.message || err.message || "Erreur d'inscription";
        setError(message);

        return { success: false, error: message };
      }
    },
    []
  );

  // Logout
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Logout locally even if API call fails
    } finally {
      setUser(null);
      setError(null);
      window.location.replace("/login");
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (profileData) => {
    setError(null);

    try {
      const response = await authApi.updateProfile(profileData);
      const { user: updatedUser } = response.data;

      setUser(updatedUser);

      return { success: true, user: updatedUser };
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Erreur de mise à jour";
      setError(message);

      return { success: false, error: message };
    }
  }, []);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.role === "admin";
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      login,
      register,
      logout,
      updateProfile,
      isAdmin,
    }),
    [user, isLoading, error, login, register, logout, updateProfile, isAdmin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
